import {
	requestInterceptors, responseInterceptors,
} from 'interceptors';
import {
	database, TableName, upsertToDB,
} from 'utils/database';
import {
	HeaderValue, HttpInterceptor, HttpOptions, HttpRequestModel, HttpRequestType, Interceptors,
} from '.';
import {
	getHttpReadyObject,
} from '../object';
import {
	Collection,
	IndexableType,
	Table,
} from 'dexie';
import {
	responseHasChanges,
} from './http-client.util';



export const getBasePath = () => {
	const {
		hostname,
		protocol,
	} = window?.location || {
		// Just in case it's undefined for testing or something
		hostname: 'localhost',
		protocol: 'https:',
	};
	if (hostname === 'localhost' || process.env.NODE_ENV === 'development') {
		return `${protocol}//${hostname}:13014/`;
	}
	return `${protocol}//${hostname}/`;
};


/**
 * A wrapper for API calls
 * - Allows the use of interceptors
 * @constructor
 */
const HttpClient = () => {
	const BASE_PATH = getBasePath();
	const interceptors: Interceptors = new Interceptors();
	const defaultHeaders = {
		Accept: HeaderValue.APPLICATION_JSON,
		'Content-Type': HeaderValue.APPLICATION_JSON,
	};

	/**
   * A simple check to see if the client is online or not
   */
	const isOnline = (): boolean => window?.navigator?.onLine;

	/**
   * Performing the act of intercepting the requests
   * @param interceptors - The list of interceptors to process
   * @param url - The URL can be used to limit what URLS to intercept for etc
   * @param input - The request or response content to intercept
   */
	const intercept = async <T>(interceptors: HttpInterceptor<T>[], url: string, input: T) => {
		let result: T = input;

		for (const interceptor of interceptors) {
			result = await interceptor(url, result);
		}

		return result;
	};

	/**
   * Intercepts http calls before they are called
   * Allowing you to change the headers etc
   * @param interceptor
   */
	const addInterceptorRequest = (interceptor: HttpInterceptor<HttpRequestModel>) =>
		interceptors.request.push(interceptor);
	/**
   * Intercepts the responses for http calls
   * @param interceptor
   */
	const addInterceptorResponse = (interceptor: HttpInterceptor<Response>) =>
		interceptors.response.push(interceptor);

	/**
	 * Resetting the fresh or updated entry
	 * @param indexedDBId
	 * @param table
	 * @param method
	 * @param original
	 */
	const handleFailedResponseEntry = async (
		indexedDBId: IndexableType | undefined | void,
		table: Table | undefined,
		method: HttpRequestType,
		original: any
	)  => {
		try {
			/**
			 * Post requests should not be stored if they fail.
			 */
			if (method === HttpRequestType.POST && indexedDBId) {
				await table?.delete(indexedDBId);
			}
			/**
			 * If it is a patch or delete request, we should just revert to the original
			 */
			if ((method === HttpRequestType.DELETE || method === HttpRequestType.PATCH) && original) {
				await table?.put(original);
			}
		}
		catch (err) {
			console.error('Could not revert changes to DB', err);
		}
	}

	/**
   * A wrapper for fetch that returns the json object
   * so that we won't need to do .json().then everywhere
   * @param path
   * @param request - The http request and it's contents
   * @param options - Custom options for the request
   * @param id - The ID of the item to be deleted
   * @param deleteQuery - A query that will be called upon a successful get request
   */
	const httpRequest = async <T, OriginFormat = any>(
		path: string,
		request: HttpRequestModel<T>,
		options?: HttpOptions,
		id?: IndexableType,
		allowOptimisticUpdate = true,
		deleteQuery?: (result: T) => Collection<T, IndexableType>,
	): Promise<T | undefined> => {

		if (!isOnline()) {
			return upsertHttpRequestToQueue(path, request, options, id);
		}
		const defaultTimeout = 75_000;

		if (!options?.timeout && options?.timeout !== 0) {
			options = {
				...options,
				timeout: defaultTimeout,
			};
		}

		const url = `${options?.baseUrl || BASE_PATH}${path}`;
		const shouldPerformOptimisticUpdate = request.method !== HttpRequestType.GET && allowOptimisticUpdate && options.table;
		// The controller allows us to do an abort on the fetch request
		const controller = new AbortController();
		const timeoutId = setTimeout(
			() => controller.abort(),
			options?.timeout || defaultTimeout
		);
		let indexedDBId: IndexableType | undefined | void = id;
		let original: any;
		// post, put and delete should update indexeddb before the HTTP request.
		if (shouldPerformOptimisticUpdate) {
			// @ts-ignore
			const input: any = request?.body;

			if (id || input.id) {
				await options.table?.get(id || input.id)
					.then(result => original = result)
					.catch(console.error);
			}
			await upsertToDB<T>(
				input as T,
				options?.table,
				request?.method as HttpRequestType,
				id || options?.tableId
			)
				.then(id => indexedDBId = id)
				.catch(console.error);
		}

		return new Promise<T>(async (resolve, reject) => {
			/**
			 * Converting the object into string as a preperation for the fetch call
			 * and making sure that indexeddb dot path'ed objects are converted.
			 */
			request.body = getFormattedDataForContentType(request.headers, request.body);
			const opts = {
				...await intercept<HttpRequestModel>(interceptors.request, url, request),
				signal: controller.signal,
			};

			const handleResponse = async (response: any) => {
				clearTimeout(timeoutId);
				if (!response.error) {
					const interceptedResponse: OriginFormat | T = await intercept(interceptors.response, url, response);
					const transformedResponse: T = options?.transformer ?
						options.transformer<OriginFormat, T>(interceptedResponse as OriginFormat) : interceptedResponse as T;

					if (response) {
						// Updating non-optimistic data to DB
						if ((request.method === HttpRequestType.GET || !allowOptimisticUpdate) && response) {
							let hasChanges = true;
							if (deleteQuery) {
								const oldData: T[] = await deleteQuery(transformedResponse).toArray();
								hasChanges = responseHasChanges(oldData, transformedResponse as any as T[]);
								// eslint-disable-next-line max-depth
								if (hasChanges) {
									await deleteQuery(transformedResponse).delete()
										.catch(console.error);
								}
							}
							if (hasChanges) {
								await upsertToDB<T>(transformedResponse, options?.table, request?.method as HttpRequestType, id);
							}
						}
						else if (shouldPerformOptimisticUpdate && indexedDBId && response && request.method !== HttpRequestType.DELETE) {
							// Making sure the local ID is updated with the one stored in the DB
							(Array.isArray(indexedDBId) ? indexedDBId : [indexedDBId])
								.forEach(id => {

									if (Array.isArray(response)) {
										/**
										 * TODO: What do we do when the result is an array?
										 */
									}
									else if (response && response.id) {
										options?.table?.update(indexedDBId, {
											id: response.id,
										});
									}
								});
						}
					}

					resolve(transformedResponse);
				}
				else {
					await handleFailedResponseEntry(indexedDBId, options?.table, request.method as HttpRequestType, original);
					reject(response?.error);
				}
				clearTimeout(timeoutId);
			};

			fetch(url, opts)
				.then(async response => {
					if (response.status < 200 || response.status > 300) {
						await handleFailedResponseEntry(indexedDBId, options?.table, request.method as HttpRequestType, original);
						response.json()
							.then(reject)
							.catch(reject)
						return;
					}
					if (!options?.discardResponse && request.method !== HttpRequestType.DELETE) {
						response.json()
							.then(response => handleResponse(response))
							.catch(error => {
								clearTimeout(timeoutId);
								reject(error);
							});
					}
					else {
						resolve({
						} as T);
					}
				})
				.catch(error => {
					clearTimeout(timeoutId);
					reject(error);
				});
		});
	};

	/**
   * Sets the a HTTP request into a queue
   * @param url
   * @param request
   * @param options
   * @param id
   */
	const upsertHttpRequestToQueue = (
		url: string,
		request: HttpRequestModel,
		options?: HttpOptions,
		id?: IndexableType
	): Promise<undefined> => {
		try {
			return new Promise<undefined>((resolve, reject) => {

				/**
           * Defining timeout > 0 indicates that we don't wish to queue the API call
           * Requests using transformers are not supported for API call queuing
           */
				if (request.method === HttpRequestType.GET || options?.timeout || options?.transformer) {
					resolve(undefined);
					return;
				}
				const tableName: TableName = (typeof options?.table === 'string' ? options?.table : options?.table?.name) as TableName;

				return database.apiQueue.add({
					timestamp: +new Date(),
					url: url.replace(id ? `/${id}` : '', ''),
					request,
					options: options ? {
						...options,
						table: tableName,
					} : undefined,
					id,
				})
					.then(async () => resolve(undefined))
					.catch(error => {
						console.error('http.upsertHttpRequestToQueue', error);
						reject(error);
					});
			}
			);
		}
		catch (e) {
			return new Promise<undefined>(resolve => resolve(undefined));
		}
	};

	/**
   * Shorthand for stringifying JSON for application/json headers
   * @param headers
   * @param body
   */
	const getFormattedDataForContentType = <T>(headers: any, body: T): any =>
		headers['Content-Type'] === HeaderValue.APPLICATION_JSON ?
			JSON.stringify(getHttpReadyObject(body)) : body;

	/**
   * Should have options for other base urls etc??
   * @param path
   * @param customOptions
	 * @param deleteQuery - A query that will be called upon a successful get request
   */
	const get = <T, OriginFormat = any>(
		path: string,
		customOptions?: HttpOptions,
		deleteQuery?: (result: T) => Collection<T, IndexableType>,
	): Promise<T | undefined> => {
		const headers = customOptions?.headers || defaultHeaders;
		const request = {
			method: HttpRequestType.GET,
			headers,
		};
		return httpRequest<T, OriginFormat>(
			path,
			request,
			customOptions,
			undefined,
			undefined,
			deleteQuery
		);
	};

	/**
   * Perform a post request
   * @param path
   * @param body
   * @param customOptions
   */
	const post = <T, OriginFormat = any>(
		path: string,
		body: any,
		customOptions?: HttpOptions,
		allowOptimisticUpdate = true,
	): Promise<T | undefined> => {
		const headers = customOptions?.headers || defaultHeaders;
		let request = {
			headers,
			method: HttpRequestType.POST,
			body,
		};

		return httpRequest<T, OriginFormat>(
			path,
			request,
			customOptions,
			customOptions?.tableId,
			allowOptimisticUpdate
		);
	};

	/**
   * Perform a patch request
   * @param path
   * @param body
   * @param customOptions
   */
	const patch = <T, OriginFormat = any>(
		path: string,
		body: any,
		customOptions?: HttpOptions,
		allowOptimisticUpdate = true,
	): Promise<T | undefined> => {
		const headers = customOptions?.headers || defaultHeaders;
		let request = {
			headers,
			method: HttpRequestType.PATCH,
			body,
		};

		return httpRequest<T, OriginFormat>(
			path,
			request,
			customOptions,
			customOptions?.tableId,
			allowOptimisticUpdate,
		);
	};

	/**
   * Perform a put request
   * @param path
   * @param body
   * @param customOptions
   */
	const put = <T, OriginFormat = any>(
		path: string,
		body: any,
		customOptions?: HttpOptions,
		allowOptimisticUpdate = true,
	): Promise<T | undefined> => {
		const headers = customOptions?.headers || defaultHeaders;
		let request = {
			headers,
			method: HttpRequestType.PUT,
			body,
		};

		return httpRequest<T, OriginFormat>(
			path,
			request,
			customOptions,
			customOptions?.tableId,
			allowOptimisticUpdate
		);
	};

	/**
   * Perform a delete request against the backend.
   * @param path
   * @param id - The ID of the entry to delete -> Required so that we easily can delete in indexedDB
   * @param customOptions
   */
	const deleteRequest = <T, OriginFormat = any>(
		path: string,
		id: string | number,
		customOptions?: HttpOptions,
		allowOptimisticUpdate = true,
	): Promise<T | undefined> => {
		const headers = customOptions?.headers || defaultHeaders;
		let request = {
			headers,
			method: HttpRequestType.DELETE,
		};

		return httpRequest<T, OriginFormat>(
			`${path}/${id}`,
			request,
			customOptions,
			id,
			allowOptimisticUpdate,
		);
	};

	return {
		addInterceptorRequest,
		addInterceptorResponse,
		get,
		post,
		patch,
		put,
		delete: deleteRequest,
	};
};

/**
 * A wrapper for API calls
 */
export const http = HttpClient();

/**
 * Applying the interceptors
 */
requestInterceptors.forEach(interceptor => http.addInterceptorRequest(interceptor));
responseInterceptors.forEach(interceptor => http.addInterceptorResponse(interceptor));